import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { BarChart3, Eye, Disc3, ChevronRight } from "lucide-react";
import { API_BASE, thumbUrl } from "@/lib/api";
import { BrandLockup } from "@/components/Brand";
import { CampaignReport } from "@/components/CampaignReport";
import { PlatformBadge } from "@/components/PlatformBadge";
import { PressSection } from "@/components/PressSection";
import { EditorialPlacements } from "@/components/EditorialPlacements";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtFull } from "@/lib/format";
import { DASH_ZERO_CAMPAIGN_ID, DASH_ZERO_SONG_ID, DASH_ZERO_SHARE_TOKEN } from "@/lib/dashZeros";

function SongRollups({ songs, artistImage }) {
  return (
    <div className="mt-10">
      <h2 className="mb-4 font-display text-xl font-bold">
        Song rollouts <span className="text-neutral-400">({songs.length})</span>
      </h2>
      <div className="space-y-5">
        {songs.map((s, i) => (
          <motion.div
            key={s.id}
            data-testid={`share-song-rollup-${s.id}`}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            className="pb-card overflow-hidden p-5"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-neutral-900">
                {(s.cover_art || artistImage) ? (
                  <img src={thumbUrl(s.cover_art || artistImage, 160)} alt={s.title} width={80} height={80} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full place-items-center text-neutral-600"><Disc3 className="h-7 w-7" /></div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-xl font-bold leading-tight">{s.title}</h3>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {s.platforms.map((p) => <PlatformBadge key={p} platform={p} />)}
                  <span className="text-sm text-neutral-500">
                    · {s.campaign_count} campaign{s.campaign_count === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <div className="rounded-2xl border border-black/[0.06] bg-neutral-50 px-4 py-2.5 text-center">
                  <p className="num-display text-lg font-extrabold">{fmtFull(s.summary.total_views)}</p>
                  <p className="text-[11px] text-neutral-500">Views</p>
                </div>
                <div className="rounded-2xl border border-black/[0.06] bg-neutral-50 px-4 py-2.5 text-center">
                  <p className="num-display text-lg font-extrabold">{s.summary.post_count}</p>
                  <p className="text-[11px] text-neutral-500">Posts</p>
                </div>
                <div className="rounded-2xl border border-black/[0.06] bg-neutral-50 px-4 py-2.5 text-center">
                  <p className="num-display text-lg font-extrabold">{s.summary.avg_engagement_rate}%</p>
                  <p className="text-[11px] text-neutral-500">Avg ER</p>
                </div>
              </div>
            </div>

            {s.campaigns.length > 0 && (
              <div className="mt-4 space-y-2 border-t border-black/[0.06] pt-4">
                {s.campaigns.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`share-run-${c.id}`}
                    className="flex items-center gap-3 rounded-xl bg-neutral-50/70 px-3 py-2.5"
                  >
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-neutral-900">
                      {(c.cover_art || artistImage) ? (
                        <img src={thumbUrl(c.cover_art || artistImage, 120)} alt="" width={40} height={40} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                      ) : (
                        <div className="grid h-full place-items-center text-neutral-600"><Disc3 className="h-4 w-4" /></div>
                      )}
                    </div>
                    <PlatformBadge platform={c.platform} />
                    <p className="min-w-0 flex-1 truncate text-sm font-medium">{c.title}</p>
                    <p className="shrink-0 text-xs text-neutral-500">
                      {c.summary.post_count} posts · {fmtFull(c.summary.total_views)} views
                    </p>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function SharePage() {
  const { campaignId, songId, artistId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const isSong = Boolean(songId);
  const isArtist = Boolean(artistId);

  useEffect(() => {
    let url;
    if (isArtist) url = `${API_BASE}/public/artists/${artistId}`;
    else if (isSong) url = `${API_BASE}/public/songs/${songId}`;
    else url = `${API_BASE}/public/campaigns/${campaignId}`;
    axios.get(url).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [campaignId, songId, artistId, isSong, isArtist]);

  const title = isSong ? data?.song?.title : data?.campaign?.title;
  const artistName = isSong ? data?.artist?.name : data?.campaign?.artist;
  // Scope dash-for-zero to the Merges "Another State" campaign share and song share only.
  const dashZeros =
    [DASH_ZERO_CAMPAIGN_ID, DASH_ZERO_SHARE_TOKEN].includes(campaignId) ||
    songId === DASH_ZERO_SONG_ID ||
    data?.campaign?.id === DASH_ZERO_CAMPAIGN_ID ||
    data?.song?.id === DASH_ZERO_SONG_ID;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-[#E7DFCC] bg-[#F7EFDD]/88 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
          <BrandLockup />
          <span data-testid="shared-badge" className="flex items-center gap-1.5 rounded-full border border-[#E7DFCC] bg-white px-3 py-1 text-xs font-medium text-[#6B7A80]">
            <Eye className="h-3.5 w-3.5" /> Read-only shared report
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {notFound ? (
          <div className="pb-card mt-10 flex flex-col items-center gap-3 p-16 text-center">
            <BarChart3 className="h-10 w-10 text-neutral-300" />
            <p className="font-display text-xl font-bold">Report not found</p>
            <p className="text-sm text-neutral-500">This shared link is invalid or was removed.</p>
          </div>
        ) : !data ? (
          <div className="space-y-6">
            <Skeleton className="h-40 rounded-2xl" />
            <div className="grid grid-cols-3 gap-6"><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-40 rounded-2xl" /></div>
          </div>
        ) : isArtist ? (
          <>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="pb-card flex flex-col gap-5 p-6 sm:flex-row sm:items-center" data-testid="share-artist-header">
              <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl bg-neutral-900 shadow-lg ring-1 ring-black/5 sm:h-32 sm:w-32">
                {data.artist.image ? (
                  <img src={thumbUrl(data.artist.image, 320)} alt={data.artist.name} width={128} height={128} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full place-items-center text-neutral-600"><Disc3 className="h-8 w-8" /></div>
                )}
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#6E97A2]">Artist rollout</p>
                <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{data.artist.name}</h1>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {data.platforms.map((p) => <PlatformBadge key={p} platform={p} />)}
                  <span className="text-sm text-neutral-500">
                    · {data.songs.length} song{data.songs.length === 1 ? "" : "s"} tracked
                  </span>
                </div>
              </div>
            </motion.div>

            <div className="mt-6">
              <CampaignReport data={data} hideHeader middleSlot={<SongRollups songs={data.songs} artistImage={data.artist?.image} />} artistImage={data.artist?.image} />
            </div>
            {(() => {
              const es = (data.songs || []).find((s) => Array.isArray(s.editorial_placements) && s.editorial_placements.length > 0);
              return es ? (
                <EditorialPlacements className="mt-12" placements={es.editorial_placements} source={es.editorial_placements_source} />
              ) : null;
            })()}
            {data.press?.length > 0 && <PressSection className="mt-12" items={data.press} />}
          </>
        ) : (
          <>
            {isSong && (
              <div className="mb-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#6E97A2]">Song rollout</p>
                <h1 className="font-display text-3xl font-extrabold tracking-tight">{title} <span className="text-neutral-400">— {artistName}</span></h1>
              </div>
            )}
            <CampaignReport data={data} hideHeader={isSong} artistImage={data.artist?.image} dashZeros={dashZeros} />
            {isSong && (
              <EditorialPlacements
                className="mt-12"
                placements={data.song?.editorial_placements}
                source={data.song?.editorial_placements_source}
              />
            )}
            {isSong && data.press?.length > 0 && <PressSection className="mt-12" items={data.press} />}
          </>
        )}
        <p className="mt-12 text-center text-xs text-[#9aa7ab]">Powered by Playbook Distro · Sound-performance analytics</p>
      </main>
    </div>
  );
}
